import { useEffect, useRef, useState } from "react";
import { assetFor } from "../render/assets";
import { renderPetScene } from "../render/draw";
import type { BookEntryView } from "./view-model";
import { PixelIcon } from "./PixelIcon";

export interface BookViewProps {
  book: BookEntryView[];
  onClose(): void;
  onVisit?(id: string): void;
  readOnly?: boolean;
  title?: string;
  initialPage?: number;
}

/** Detect horizontal swipe over 40px and lock when vertical scroll is greater. */
export function resolveBookSwipe(
  start: { x: number; y: number },
  end: { x: number; y: number },
): "prev" | "next" | null {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
    return deltaX < 0 ? "next" : "prev";
  }
  return null;
}

/** A still picture of a book pet in its tank. */
function Thumb({ entry }: { entry: BookEntryView }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas && typeof canvas.getContext === "function" ? canvas.getContext("2d") : null;
    if (!ctx) return;
    renderPetScene(ctx, { asset: assetFor(entry.species), mood: "happy", tick: 0, reducedMotion: true, looks: entry.looks });
  }, [entry.looks, entry.species]);
  return <canvas ref={ref} width={80} height={60} role="img" aria-label={`${entry.name} the ${entry.species}`} />;
}

/**
 * The picture book (docs/handoff/006-shell-reference.html `#bookview`): rustic desk, leather cover, cream paper,
 * flip page-by-page with arrows, swipe, dots, or arrow keys.
 */
export function BookView({ book, onClose, onVisit, readOnly = false, title, initialPage = 0 }: BookViewProps) {
  const [pageIndex, setPageIndex] = useState(initialPage);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const totalPages = book.length;
  const safePageIndex = totalPages === 0 ? 0 : Math.max(0, Math.min(pageIndex, totalPages - 1));

  const goPrev = () => {
    setPageIndex((prev) => Math.max(0, prev - 1));
  };

  const goNext = () => {
    setPageIndex((prev) => Math.min(totalPages - 1, prev + 1));
  };

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (totalPages <= 1) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setPageIndex((prev) => Math.max(0, prev - 1));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setPageIndex((prev) => Math.min(totalPages - 1, prev + 1));
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [totalPages]);

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (t) {
      touchStartRef.current = { x: t.clientX, y: t.clientY };
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    if (!touchStartRef.current || !t) return;
    const end = { x: t.clientX, y: t.clientY };
    const swipe = resolveBookSwipe(touchStartRef.current, end);
    touchStartRef.current = null;
    if (swipe === "next") goNext();
    else if (swipe === "prev") goPrev();
  };

  const heading = readOnly ? (title || "Picture book") : "My picture book";

  return (
    <section
      className="bookview bv-desk"
      data-testid="book-view"
      aria-labelledby="bvh"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="bv-head">
        <h2 id="bvh">{heading}</h2>
        {readOnly ? (
          <button ref={closeRef} className="btn" type="button" data-testid="book-back" onClick={onClose}>
            Back to friends
          </button>
        ) : (
          <button ref={closeRef} className="btn" type="button" data-testid="book-close" onClick={onClose}>
            Take my shell out
          </button>
        )}
      </div>

      <div className="bv-cover">
        <div className="bv-ribbon" aria-hidden="true" />
        <div className="bv-pages">
          <div className="bv-spine" aria-hidden="true" />
          {totalPages === 0 ? (
            <div className="bv-blank-page">
              <p className="bv-empty" data-testid="book-empty">
                Nothing here yet. A pet that makes it to the end of the week goes into your book.
              </p>
            </div>
          ) : (
            book.map((entry, i) => (
              <div
                className={`card bv-page ${i === safePageIndex ? "bv-page-current" : ""}`}
                key={entry.id}
                data-testid="book-card"
                data-page={i}
                hidden={i !== safePageIndex}
                aria-hidden={i !== safePageIndex ? "true" : undefined}
              >
                <div className="bv-polaroid">
                  <span className="bv-tape" aria-hidden="true" />
                  <Thumb entry={entry} />
                </div>
                <h3 className="bv-pet-name">{entry.name}</h3>
                <p className="bv-pet-stats">{`${entry.weekLabel} · ${entry.species.charAt(0).toUpperCase() + entry.species.slice(1)} · ${entry.points} points`}</p>
                {!readOnly && onVisit ? (
                  <button
                    className="btn bv-visit-btn"
                    type="button"
                    aria-label={`Visit ${entry.name}`}
                    data-testid={`visit-${entry.id}`}
                    onClick={() => onVisit(entry.id)}
                  >
                    Visit
                  </button>
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>

      {totalPages > 0 ? (
        <div className="bv-nav">
          <button
            type="button"
            className="bv-arrow bv-prev"
            aria-label="Previous page"
            disabled={safePageIndex === 0}
            onClick={goPrev}
          >
            <PixelIcon name="prev" size={24} />
          </button>

          <div className="bv-nav-center">
            <span className="bv-counter">{`Page ${safePageIndex + 1} of ${totalPages}`}</span>
            <div className="bv-dots" role="tablist" aria-label="Book pages">
              {book.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`bv-dot ${i === safePageIndex ? "active" : ""}`}
                  aria-label={`Go to page ${i + 1}`}
                  aria-current={i === safePageIndex ? "page" : undefined}
                  onClick={() => setPageIndex(i)}
                />
              ))}
            </div>
          </div>

          <button
            type="button"
            className="bv-arrow bv-next"
            aria-label="Next page"
            disabled={safePageIndex >= totalPages - 1}
            onClick={goNext}
          >
            <PixelIcon name="next" size={24} />
          </button>
        </div>
      ) : null}

      <div className="bv-note">
        {readOnly
          ? "Pets that were lost are not added to the book."
          : "Pets that were lost are not added to the book. Visit a pet to play with it: no score, no meters."}
      </div>
    </section>
  );
}
